import { VASTClient, VASTTracker } from '@dailymotion/vast-client';

/**
 * Simple VAST ad manager
 */
class VASTManager {
    constructor() {
        this.vastClient = new VASTClient();
        this.vastTracker = null;
        this.adVideo = null;
        this.adData = null;
        this.clickHandled = false;
        this.callbacks = {
            onAdStart: null,
            onAdEnd: null,
            onAdError: null,
            onAdClick: null,
        };
    }

    /**
     * Set ad video element
     */
    setAdVideo(adVideo) {
        this.adVideo = adVideo;
    }

    /**
     * Load VAST ad from proxy endpoint
     * The proxy handles token replacement and avoids CORS issues
     */
    async loadAd() {
        if (!this.adVideo) {
            throw new Error('Ad video element is required');
        }

        // Use proxy endpoint to avoid CORS issues
        const proxyUrl = '/api/d/vast-proxy';

        console.log('[VASTManager] Loading ad from proxy:', proxyUrl);

        try {
            const response = await this.vastClient.get(proxyUrl);
            console.log('[VASTManager] Proxy response:', response);
            
            if (!response?.ads?.length) {
                throw new Error('No ads in VAST response');
            }

            const ad = response.ads[0];
            const creative = ad.creatives.find(c => c.type === 'linear');
            
            if (!creative) {
                throw new Error('No linear creative found');
            }

            const mediaFile = creative.mediaFiles.find(
                mf => mf.mimeType === 'video/mp4' && mf.fileURL
            );

            if (!mediaFile) {
                throw new Error('No compatible media file found');
            }

            // Store ad data
            this.adData = {
                ad,
                creative,
                mediaFile,
                clickThroughUrl: this.extractClickThroughUrl(creative),
            };

            // Create tracker
            this.vastTracker = new VASTTracker(this.vastClient, ad, creative);

            // Setup VAST tracker event listeners for tracking confirmation
            this.setupVastTrackerListeners();

            return this.adData;
        } catch (error) {
            console.error('[VASTManager] Error loading ad:', error);
            throw error;
        }
    }

    /**
     * Play the loaded ad
     */
    async playAd() {
        if (!this.adData || !this.adVideo) {
            throw new Error('No ad loaded or video element not set');
        }

        // Set video source
        this.adVideo.src = this.adData.mediaFile.fileURL;
        
        // Setup event listeners
        this.setupAdEventListeners();

        // Play the ad
        try {
            await this.adVideo.play();
            return true;
        } catch (error) {
            this.cleanup();
            throw error;
        }
    }

    /**
     * Skip the current ad
     */
    skipAd() {
        if (this.vastTracker) {
            try {
                this.vastTracker.skip();
            } catch (e) {
                console.warn('[VASTManager] Failed to track skip:', e);
            }
        }
        this.cleanup();
        this.callbacks.onAdEnd?.();
    }

    /**
     * Handle ad click
     */
    handleAdClick() {
        if (!this.adData?.clickThroughUrl || this.clickHandled) return;
        
        this.clickHandled = true;

        // Track click
        if (this.vastTracker) {
            try {
                this.vastTracker.click();
            } catch (e) {
                console.warn('[VASTManager] Failed to track click:', e);
            }
        }

        // Open URL
        const link = document.createElement('a');
        link.href = this.adData.clickThroughUrl;
        link.target = '_blank';
        link.rel = 'noopener nofollow';
        link.className = 'exclude-poppit';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.callbacks.onAdClick?.();

        // Clean up and end ad directly (don't call skipAd to avoid recursion)
        this.cleanup();
        
        console.log('[VASTManager] Calling onAdEnd callback after click');
        this.callbacks.onAdEnd?.();
    }

    /**
     * Extract click-through URL from creative
     */
    extractClickThroughUrl(creative) {
        // Try different possible locations
        let url = creative.videoClickThroughURLTemplate?.url ||
                  creative.videoClickThroughURLTemplate ||
                  creative.videoClicks?.clickThrough?.url ||
                  creative.videoClicks?.clickThrough;
        
        // If it's an object, try to extract the URL
        if (url && typeof url === 'object' && 'url' in url) {
            url = url.url;
        }
        
        return url || null;
    }

    /**
     * Setup VAST tracker event listeners for tracking confirmation
     */
    setupVastTrackerListeners() {
        if (!this.vastTracker) return;

        // Listen for impression tracking confirmation
        this.vastTracker.on('creativeView', () => {
            console.log('[VASTManager] ✓ Impression tracked successfully');
        });

        // Listen for click tracking confirmation
        this.vastTracker.on('clickthrough', () => {
            console.log('[VASTManager] ✓ Click tracked successfully');
        });

        // Listen for quartile tracking events
        this.vastTracker.on('start', () => {
            console.log('[VASTManager] ✓ Start tracked successfully');
        });

        this.vastTracker.on('firstQuartile', () => {
            console.log('[VASTManager] ✓ First quartile tracked successfully');
        });

        this.vastTracker.on('midpoint', () => {
            console.log('[VASTManager] ✓ Midpoint tracked successfully');
        });

        this.vastTracker.on('thirdQuartile', () => {
            console.log('[VASTManager] ✓ Third quartile tracked successfully');
        });

        this.vastTracker.on('complete', () => {
            console.log('[VASTManager] ✓ Complete tracked successfully');
        });

        this.vastTracker.on('skip', () => {
            console.log('[VASTManager] ✓ Skip tracked successfully');
        });

        this.vastTracker.on('mute', () => {
            console.log('[VASTManager] ✓ Mute tracked successfully');
        });

        this.vastTracker.on('unmute', () => {
            console.log('[VASTManager] ✓ Unmute tracked successfully');
        });

        // Listen for any tracking errors
        this.vastTracker.on('error', (error) => {
            console.warn('[VASTManager] ⚠️ VAST tracking error:', error);
        });
    }

    /**
     * Setup ad event listeners
     */
    setupAdEventListeners() {
        if (!this.adVideo) return;

        // Track impression when ad can actually play
        const onCanPlay = () => {
            if (this.vastTracker) {
                try {
                    this.vastTracker.trackImpression();
                } catch (e) {
                    console.warn('[VASTManager] Failed to track impression:', e);
                }
            }
            this.adVideo.removeEventListener('canplay', onCanPlay);
        };

        // Handle when ad starts playing
        const onPlay = () => {
            this.callbacks.onAdStart?.();
        };

        // Progress tracking
        const onTimeUpdate = () => {
            if (this.vastTracker && this.adVideo.currentTime > 0) {
                try {
                    this.vastTracker.setProgress(this.adVideo.currentTime);
                } catch (e) {
                    console.warn('[VASTManager] Failed to track progress:', e);
                }
            }
        };

        // Completion tracking
        const onEnded = () => {
            if (this.vastTracker) {
                try {
                    this.vastTracker.complete();
                } catch (e) {
                    console.warn('[VASTManager] Failed to track completion:', e);
                }
            }
            this.cleanup();
            this.callbacks.onAdEnd?.();
        };

        // Error tracking
        const onError = () => {
            if (this.vastTracker) {
                try {
                    this.vastTracker.errorWithCode(405); // Problem displaying MediaFile
                } catch (e) {
                    console.warn('[VASTManager] Failed to track error:', e);
                }
            }
            this.cleanup();
            this.callbacks.onAdError?.();
        };

        // Volume tracking
        const onVolumeChange = () => {
            if (this.vastTracker) {
                try {
                    if (this.adVideo.muted || this.adVideo.volume === 0) {
                        this.vastTracker.setMuted();
                    } else {
                        this.vastTracker.setUnmuted();
                    }
                } catch (e) {
                    console.warn('[VASTManager] Failed to track volume change:', e);
                }
            }
        };

        // Add listeners
        this.adVideo.addEventListener('canplay', onCanPlay);
        this.adVideo.addEventListener('play', onPlay);
        this.adVideo.addEventListener('timeupdate', onTimeUpdate);
        this.adVideo.addEventListener('ended', onEnded);
        this.adVideo.addEventListener('error', onError);
        this.adVideo.addEventListener('volumechange', onVolumeChange);

        // Store references for cleanup
        this._listeners = {
            canplay: onCanPlay,
            play: onPlay,
            timeupdate: onTimeUpdate,
            ended: onEnded,
            error: onError,
            volumechange: onVolumeChange,
        };
    }

    /**
     * Remove ad event listeners
     */
    removeAdEventListeners() {
        if (!this.adVideo || !this._listeners) return;

        Object.entries(this._listeners).forEach(([event, handler]) => {
            this.adVideo.removeEventListener(event, handler);
        });

        this._listeners = null;
    }

    /**
     * Set callbacks
     */
    on(event, callback) {
        if (event in this.callbacks) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * Get ad duration
     */
    getDuration() {
        return this.adVideo?.duration || 0;
    }

    /**
     * Get ad current time
     */
    getCurrentTime() {
        return this.adVideo?.currentTime || 0;
    }

    /**
     * Check if skip is available (after 5 seconds)
     */
    canSkip() {
        return this.getCurrentTime() > 5;
    }

    /**
     * Cleanup
     */
    cleanup() {
        this.removeAdEventListeners();
        if (this.adVideo) {
            this.adVideo.pause();
            this.adVideo.src = '';
        }
        this.vastTracker = null;
        this.adData = null;
        this.clickHandled = false;
    }
}

export default VASTManager;